
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import React, { useMemo } from 'react';
import flush from 'just-flush';
import { GetStaticPaths, GetStaticPathsResult, GetStaticProps } from 'next';
import { useQuery } from 'urql';
import { NewsGrid } from '@stereoagency/ui';
import useTranslation from 'next-translate/useTranslation';
import compact from 'just-compact';
import { clientFactory, useFragment } from '@/gql';
import { withPrepass } from '@/utils';
import { FindAllCategoriesQueryDocument, FindPostSummariesForCategorySlugQueryDocument, } from '@/gql/client/graphql';
import { MediaItemFieldsFragment, PostSummaryFieldsFragment, TermNodeFieldsFragment, } from '@/gql/documents/fragments';
import { i18n } from '@/config';
import { Layout, SeoHead } from '@/components';
import { useLangCodeFilter, useMediaItems } from '@/hooks';
type UrlParams = {
    category: string[];
};
type PageProps = {
    category: string;
    preview?: boolean;
};
export default function Page({ category, preview }: PageProps) {
    const langCode = useLangCodeFilter();
    const { t } = useTranslation();
    const [{ data }] = useQuery({
        query: FindPostSummariesForCategorySlugQueryDocument,
        variables: { slug: category, lang: langCode }
    });
    const postSummaries = useFragment(PostSummaryFieldsFragment, flush(data?.posts?.nodes ?? []));
    const mediaItems = useMediaItems(useMemo(() => (flush(postSummaries.map((post) => {
        const mediaItem = useFragment(MediaItemFieldsFragment, post.featuredImage?.node);
        return mediaItem?.databaseId ?? null;
    }))), [postSummaries]));
    if (!data || !postSummaries.length)
        return null;
    return (<Layout preview={preview}>
      <SeoHead title={category}/>
      <NewsGrid label={t('pages--blog:label')} title={category} cards={flush(postSummaries.map((post) => {
            const categoriesFields = useFragment(TermNodeFieldsFragment, flush(post.categories?.nodes ?? []));
            const mediaItemFields = useFragment(MediaItemFieldsFragment, post.featuredImage?.node);
            const mediaItem = mediaItemFields?.databaseId ? mediaItems.get(mediaItemFields.databaseId) : null;
            if (!mediaItem?.mediaItemUrl)
                return null;
            return {
                title: post.title ?? '',
                labels: flush(categoriesFields.map((cat) => (cat.name ? {
                    label: cat.name,
                    href: cat.uri ?? '/'
                } : null))),
                image: {
                    src: mediaItem.mediaItemUrl,
                    width: mediaItem.mediaDetails?.width ?? 320,
                    height: mediaItem.mediaDetails?.height ?? 200
                },
                callToAction: {
                    label: t('global:read-more'),
                    href: post.uri ?? '/'
                }
            };
        }))}/>
    </Layout>);
}
export const getStaticPaths: GetStaticPaths<UrlParams> = async () => {
    type PathsType = GetStaticPathsResult<UrlParams>['paths'];
    const client = clientFactory();
    const allPages = await client.query(FindAllCategoriesQueryDocument, {}).toPromise();
    const nodes = allPages.data?.categories?.nodes ?? [];
    return {
        paths: flush(nodes.reduce<PathsType>((acc, node) => {
            const fields = useFragment(TermNodeFieldsFragment, node);
            const uriMatch = fields?.uri ? fields.uri.match(/^\/([a-z]{2})?\/blog\/category\/(.+?)\/$/) : null;
            if (!uriMatch)
                return acc;
            i18n.locales.forEach((locale) => {
                acc.push({
                    locale,
                    params: { category: compact(uriMatch[2]!.split('/')) }
                });
            });
            return acc;
        }, [])),
        fallback: 'blocking'
    };
};
const getStaticProps: GetStaticProps<PageProps, UrlParams> = async ({ preview, params, locale }) => {
    if (!params?.category) {
        return {
            notFound: true
        };
    }
    return withPrepass({
        params,
        preview,
        locale,
        propsFactory: async () => {
            if (!params?.category.length) {
                return { notFound: true };
            }
            return {
                category: params.category.join('/')
            };
        },
        Component: Page
    });
};

    async function __Next_Translate__getStaticProps__1a1a7090bda__(ctx) {
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/blog/category/[...category]',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a1a7090bda__ as getStaticProps }
  